import { useState } from "react";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatTime, formatDate, getToday } from "@/lib/utils";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

export function BookingsList() {
  const [date, setDate] = useState(getToday());

  const { data: bookings, isLoading } = trpc.bookings.listAll.useQuery({ date });
  const utils = trpc.useUtils();

  const cancelBooking = trpc.bookings.cancel.useMutation({
    onSuccess: () => {
      utils.bookings.listAll.invalidate();
    },
  });

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-lg font-semibold">Bookings</h2>
        <Input
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-auto"
        />
      </div>

      <p className="mb-3 text-sm text-muted-foreground">{formatDate(date)}</p>

      {isLoading ? (
        <div className="animate-pulse space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-16 rounded bg-muted" />
          ))}
        </div>
      ) : !bookings?.length ? (
        <p className="text-sm text-muted-foreground">No bookings for this date.</p>
      ) : (
        <div className="space-y-2">
          {bookings.map((booking) => (
            <div
              key={booking.id}
              className={cn(
                "flex items-center justify-between rounded-lg border p-4",
                booking.status === "cancelled"
                  ? "border-border/50 bg-muted/50 opacity-60"
                  : "border-border bg-card",
              )}
            >
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-medium">{booking.title}</p>
                  <span
                    className={cn(
                      "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium",
                      booking.status === "confirmed" && "bg-farmless-green-3 text-farmless-green",
                      booking.status === "cancelled" && "bg-red-100 text-red-700",
                      booking.status === "tentative" && "bg-yellow-100 text-yellow-700",
                    )}
                  >
                    {booking.status}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground">
                  {booking.room_name} &middot; {booking.organizer_name}
                </p>
                <p className="text-sm text-muted-foreground">
                  {formatTime(booking.start_time)} - {formatTime(booking.end_time)}
                </p>
              </div>
              {booking.status === "confirmed" && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => cancelBooking.mutate({ id: booking.id })}
                  disabled={cancelBooking.isPending}
                >
                  <X className="h-4 w-4 text-destructive" />
                </Button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
